import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Eye, Sparkles, Star, Clock, Users } from 'lucide-react';
interface LandingPhaseProps {
  onNext: () => void;
}
// Animated counter that ticks up to a target number
const AnimatedCounter = ({
  target
}: {
  target: number;
}) => {
  const [count, setCount] = useState(target - 250);
  useEffect(() => {
    // Initial ramp-up animation
    const rampDuration = 1800;
    const startValue = target - 250;
    const startTime = performance.now();
    let rafId: number;
    const tick = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / rampDuration, 1);
      // ease-out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = Math.floor(startValue + (target - startValue) * eased);
      setCount(current);
      if (progress < 1) {
        rafId = requestAnimationFrame(tick);
      } else {
        // After reaching target, slowly tick upward to simulate live activity
        const liveInterval = setInterval(() => {
          setCount(c => c + Math.floor(Math.random() * 3) + 1);
        }, 2500);
        (tick as any)._interval = liveInterval;
      }
    };
    rafId = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(rafId);
      if ((tick as any)._interval) clearInterval((tick as any)._interval);
    };
  }, [target]);
  return <span className="tabular-nums font-semibold text-gold-light" data-id="element-18">
      +{count.toLocaleString('es-MX')}
    </span>;
};
export const LandingPhase = ({
  onNext
}: LandingPhaseProps) => {
  return <motion.div className="w-full max-w-3xl mx-auto px-4 py-12 flex flex-col items-center justify-center min-h-screen text-center" initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} exit={{
    opacity: 0,
    y: -20
  }} transition={{
    duration: 0.8
  }} data-id="element-19">
      {/* Ornamental mystical eye visual */}
      <motion.div className="relative mb-8" initial={{
      scale: 0.8,
      opacity: 0
    }} animate={{
      scale: 1,
      opacity: 1
    }} transition={{
      duration: 1,
      delay: 0.2
    }} data-id="element-20">
        <div className="absolute inset-0 bg-gold/20 blur-3xl rounded-full" data-id="element-21" />

        <div className="relative w-32 h-32 md:w-40 md:h-40 flex items-center justify-center" data-id="element-22">
          <motion.div className="absolute inset-0 rounded-full border-2 border-gold/60" animate={{
          rotate: 360
        }} transition={{
          duration: 40,
          repeat: Infinity,
          ease: 'linear'
        }} style={{
          borderStyle: 'dashed'
        }} data-id="element-23" />
          <div className="absolute inset-3 rounded-full border border-gold/40" data-id="element-24" />
          <div className="absolute inset-0 flex items-center justify-center" data-id="element-25">
            <Star className="absolute top-1 left-1/2 -translate-x-1/2 w-3 h-3 text-gold" fill="currentColor" data-id="element-26" />
            <Star className="absolute bottom-1 left-1/2 -translate-x-1/2 w-3 h-3 text-gold" fill="currentColor" data-id="element-27" />
            <Star className="absolute left-1 top-1/2 -translate-y-1/2 w-3 h-3 text-gold" fill="currentColor" data-id="element-28" />
            <Star className="absolute right-1 top-1/2 -translate-y-1/2 w-3 h-3 text-gold" fill="currentColor" data-id="element-29" />
          </div>
          <div className="relative bg-gradient-to-br from-burgundy-dark to-ink rounded-full w-20 h-20 md:w-24 md:h-24 flex items-center justify-center border-2 border-gold shadow-gold-glow-strong" data-id="element-30">
            <Eye className="w-10 h-10 md:w-12 md:h-12 text-gold" strokeWidth={1.5} fill="rgba(201, 169, 97, 0.15)" data-id="element-31" />
          </div>
        </div>
      </motion.div>

      {/* HEADLINE */}
      <motion.h1 className="font-cinzel text-3xl md:text-5xl lg:text-6xl text-gold mb-6 leading-tight text-shadow-gold max-w-3xl" initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.8,
      delay: 0.4
    }} data-id="element-32">
        El Tarot Turco Revela <br className="hidden md:block" data-id="element-33" />
        Lo Que El Universo Ya <br className="hidden md:block" data-id="element-34" />
        Escribió Para Ti
      </motion.h1>

      {/* SUBHEADLINE */}
      <motion.p className="font-sans text-white text-lg md:text-xl max-w-2xl leading-relaxed mb-10 px-2" initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.8,
      delay: 0.6
    }} data-id="element-35">
        Responde estas preguntas y recibe tu lectura personalizada de tarot
        basada en la sabiduría ancestral de Constantinopla.
      </motion.p>

      {/* BULLET POINTS */}
      <motion.ul className="flex flex-col sm:flex-row gap-4 sm:gap-8 mb-6 text-white" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      duration: 0.8,
      delay: 0.8
    }} data-id="element-36">
        <li className="flex items-center gap-2 text-base" data-id="element-37">
          <Sparkles className="w-5 h-5 text-gold flex-shrink-0" data-id="element-38" />
          <span data-id="element-39">100% personalizada para ti</span>
        </li>
        <li className="flex items-center gap-2 text-base" data-id="element-40">
          <Users className="w-5 h-5 text-gold flex-shrink-0" data-id="element-41" />
          <span data-id="element-42">
            <AnimatedCounter target={497650} data-id="element-43" /> lecturas entregadas en todo el
            mundo
          </span>
        </li>
      </motion.ul>

      {/* CTA BUTTON — pulsing/heartbeat animation */}
      <motion.div className="relative" initial={{
      opacity: 0,
      scale: 0.9
    }} animate={{
      opacity: 1,
      scale: 1
    }} transition={{
      duration: 0.6,
      delay: 1.1
    }} data-id="element-44">
        {/* Outer pulsing rings — slower */}
        <motion.span aria-hidden="true" className="absolute inset-0 rounded-full bg-gold/40" animate={{
        scale: [1, 1.25, 1.5],
        opacity: [0.6, 0.2, 0]
      }} transition={{
        duration: 3.5,
        repeat: Infinity,
        ease: 'easeOut'
      }} data-id="element-45" />
        <motion.span aria-hidden="true" className="absolute inset-0 rounded-full bg-gold/30" animate={{
        scale: [1, 1.4, 1.7],
        opacity: [0.5, 0.15, 0]
      }} transition={{
        duration: 3.5,
        repeat: Infinity,
        ease: 'easeOut',
        delay: 1.2
      }} data-id="element-46" />

        <motion.button onClick={onNext} className="relative bg-gradient-to-b from-gold-light via-gold to-gold-dark text-ink font-cinzel font-bold text-lg md:text-xl tracking-wide px-10 py-5 rounded-full shadow-[0_10px_40px_rgba(201,169,97,0.5)] hover:shadow-[0_15px_55px_rgba(201,169,97,0.75)] transition-shadow duration-300 border-2 border-gold-light/60"
      // Heartbeat pulse: slower
      animate={{
        scale: [1, 1.06, 1, 1.03, 1]
      }} transition={{
        duration: 3,
        repeat: Infinity,
        ease: 'easeInOut',
        times: [0, 0.2, 0.4, 0.6, 1]
      }} whileHover={{
        scale: 1.08,
        y: -2
      }} whileTap={{
        scale: 0.97
      }} aria-label="Comenzar Mi Lectura" data-id="element-47">
          <span className="flex items-center gap-2" data-id="element-48">
            ✨ Comenzar Mi Lectura →
          </span>
        </motion.button>
      </motion.div>
    </motion.div>;
};